import { MAX_MAX_RESULT_COUNT } from "staticConfiguration";
import type { DataGridProProps, GridSortModel } from "@mui/x-data-grid-pro";
import type { APIError } from "core/entities";
import type { ExtractSuccessType } from "core/entities/APITypes";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useJsonFetcherWithHeaders } from "core/hooks/useJsonFetcherWithHeaders";
import { useLocale } from "core/hooks/useLocale";
import { useLocalStorage } from "core/hooks/useLocalStorage";
import { buildCentrixUrl } from "core/utils/buildCentrixUrl";
import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import type {
  PaymentRequestDto,
  PaymentRequestStatus,
} from "../entities/paymentRequestDefs";

const sortingMapping: Record<string, string> = {
  humanReadableId: "paymentRequest.humanReadableId",
  fileClaimNumber: "paymentRequest.fileClaimNumber",
  shopDetails: "shopCompany.legalName",
  applicantName: "paymentRequest.applicantName",
  insuranceCompany: "insurerCompany.legalName",
  clerkName: "clerk.name",
  cycleTime: "paymentRequest.requestedOn",
  requestedAmount: "paymentRequest.requestedAmount",
  status: "paymentRequest.status",
  requestedOn: "paymentRequest.requestedOn",
  lastModificationTime: "paymentRequest.lastModificationTime",
};

const DEFAULT_SORTING_MODEL: GridSortModel = [
  { field: "paymentRequest.humanReadableId", sort: "desc" },
];
const DEFAULT_SORTING_STRING = "paymentRequest.humanReadableId desc";
const SORTING_STORAGE_KEY = "paymentRequestSortingModel_v2";

interface PaymentRequestInfiniteQueryParams {
  maxResultCount?: number;
  shopId: string[];
  insurerId: string[];
  search: string;
  status: PaymentRequestStatus[];
}

const MAX_RESULT_COUNT = 20;

const DEFAULT_PARAMS: PaymentRequestInfiniteQueryParams = {
  maxResultCount: MAX_RESULT_COUNT,
  shopId: [],
  insurerId: [],
  search: "",
  status: [],
};

export function usePaymentRequestInfinite({
  maxResultCount = MAX_RESULT_COUNT,
  shopId,
  status,
  search,
  insurerId,
}: PaymentRequestInfiniteQueryParams = DEFAULT_PARAMS) {
  const locale = useLocale();
  const [sortModel, setSortModel] = useLocalStorage<GridSortModel>(
    SORTING_STORAGE_KEY,
    DEFAULT_SORTING_MODEL
  );

  const sortingQueryParam = useMemo(() => {
    const model = sortModel[0];
    if (!model) {
      return DEFAULT_SORTING_STRING;
    }
    const field =
      model.field in sortingMapping ? sortingMapping[model.field] : model.field;
    return `${field} ${model.sort}`;
  }, [sortModel]);

  const onSortModelChange = useCallback<
    NonNullable<DataGridProProps["onSortModelChange"]>
  >(
    (changedModel) => {
      setSortModel(changedModel);
    },
    [setSortModel]
  );

  const getKey: Parameters<
    typeof useSWRInfinite<
      ExtractSuccessType<"/api/app/payment-request">,
      APIError
    >
  >[0] = (pageIndex, previousPageData) => {
    if (previousPageData && previousPageData.items.length < maxResultCount) {
      return null;
    }
    const url = buildCentrixUrl({
      path: "/api/app/payment-request",
      parameters: {
        query: {
          MaxResultCount: maxResultCount,
          SkipCount: maxResultCount * pageIndex,
          ShopId: shopId?.length ? shopId : undefined,
          InsurerId: insurerId?.length ? insurerId : undefined,
          Status: status?.length ? status : undefined,
          Search: search || undefined,
          Sorting: sortingQueryParam,
        },
      },
      locale,
    });
    return url;
  };

  const fetcher = useJsonFetcherWithHeaders();
  const centrixFetch = useCentrixFetch();
  const { data, mutate, size, setSize, error } = useSWRInfinite<
    ExtractSuccessType<"/api/app/payment-request">,
    APIError
  >(getKey, fetcher, {
    revalidateOnMount: true,
    revalidateOnReconnect: true,
  });

  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");
  const totalNumberOfRequests = data?.[0]?.totalCount;

  const paymentRequests = useMemo(
    () => data?.flatMap(({ items }) => items) || [],
    [data]
  );

  const isLoading = isLoadingInitialData || isLoadingMore;
  const allLoaded = paymentRequests.length === totalNumberOfRequests;

  const fetchAll = useCallback(
    async (onProgress?: (progress: number) => void) => {
      async function getData(
        skipCount: number,
        reportItemCount: (count: number) => void
      ) {
        const res = await centrixFetch({
          method: "get",
          path: "/api/app/payment-request",
          parameters: {
            query: {
              MaxResultCount: MAX_MAX_RESULT_COUNT,
              SkipCount: skipCount,
              ShopId: shopId?.length ? shopId : undefined,
              InsurerId: insurerId?.length ? insurerId : undefined,
              Status: status?.length ? status : undefined,
              Search: search || undefined,
              Sorting: sortingQueryParam,
            },
          },
        });
        if (!res.ok) {
          return null;
        }
        const data = await res.json();
        reportItemCount(Array.isArray(data.items) ? data.items.length : 0);
        return data;
      }
      const firstData = await getData(0, () => {});
      if (
        !firstData ||
        !Array.isArray(firstData.items) ||
        !firstData.totalCount
      ) {
        return null;
      }

      let loadedItemCount = firstData.items.length;
      const totalCount = firstData.totalCount;
      if (loadedItemCount >= totalCount) {
        onProgress?.(100);
        return firstData;
      }
      function reportProgress() {
        onProgress?.((loadedItemCount * 100) / totalCount);
      }
      reportProgress();

      const additionalItemDataPromises = [
        ...Array(Math.ceil(totalCount / MAX_MAX_RESULT_COUNT)),
      ]
        .map((_, index) => index * MAX_MAX_RESULT_COUNT)
        .slice(1)
        .map((skipCount) =>
          getData(skipCount, (itemCount) => {
            loadedItemCount += itemCount;
            reportProgress();
          })
        );

      const additionalResponses = await Promise.all(additionalItemDataPromises);

      const additionalItems = additionalResponses
        .filter(
          (res): res is ExtractSuccessType<"/api/app/payment-request"> =>
            res !== null
        )
        .flatMap(({ items }) => items)
        .filter(
          (request): request is PaymentRequestDto =>
            request !== null && request !== undefined
        );

      return {
        totalCount: totalCount,
        items: [...firstData.items, ...additionalItems],
      };
    },
    [centrixFetch, insurerId, search, shopId, sortingQueryParam, status]
  );

  return {
    isLoadingInitialData,
    paymentRequests,
    isLoading,
    allLoaded,
    setSize,
    mutate,
    error,
    totalNumberOfRequests,
    fetchAll,
    onSortModelChange,
    sortModel,
  };
}
